import type { EventId, PlanEvent } from "../../../shared/types";
import type { EventFullData, UpdateEventData } from "../../events/types/event-form-types";
import type { PendingRecurrence } from "./use-week-calendar-state";

function extractBaseId(instanceId: string, instanceDate: string): EventId {
  const suffix = `_${instanceDate}`;
  if (instanceId.endsWith(suffix))
    return instanceId.slice(0, -suffix.length) as EventId;
  return instanceId as EventId;
}

interface RecurrenceHandlersProps {
  onDeleteEvent: (id: EventId) => void;
  onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  onUpdateEvent?: (id: EventId, updates: UpdateEventData) => void;
  onAddEventFull?: (data: EventFullData) => void;
  onResyncOccurrence?: (
    baseId: EventId,
    date: string,
    detachedId: EventId,
  ) => void;
  onResyncAllOccurrences?: (baseId: EventId) => void;
  setPendingRecurrence: (v: PendingRecurrence | null) => void;
  setPendingResync: (v: PlanEvent | null) => void;
  setEditingEvent: (v: PlanEvent | null) => void;
}

export function useRecurrenceHandlers({
  onDeleteEvent,
  onAddRecurrenceException,
  onUpdateEvent,
  onAddEventFull,
  onResyncOccurrence,
  onResyncAllOccurrences,
  setPendingRecurrence,
  setPendingResync,
  setEditingEvent,
}: RecurrenceHandlersProps) {
  const handleRecurrenceChoice = (
    action: "this" | "all",
    pendingRecurrence: PendingRecurrence,
  ) => {
    const { event } = pendingRecurrence;
    const baseId = extractBaseId(event.id as string, event.date);
    if (pendingRecurrence.kind === "delete") {
      if (action === "all") {
        onDeleteEvent(baseId);
      } else {
        onAddRecurrenceException?.(baseId, event.date);
      }
      setEditingEvent(null);
    } else {
      const { updates } = pendingRecurrence;
      if (action === "all") {
        onUpdateEvent?.(baseId, updates);
      } else {
        onAddRecurrenceException?.(baseId, event.date);
        onAddEventFull?.({
          title: event.title,
          category: event.category,
          date: (updates.date as string) ?? event.date,
          startTime: (updates.startTime as string) ?? event.startTime,
          endTime: (updates.endTime as string) ?? event.endTime,
          description: event.description,
          notes: event.notes,
          tags: [...event.tags],
          recurrence: null,
          detachedFromId: baseId,
          reminderMinutes: event.reminderMinutes,
          reminderTime: event.reminderTime ?? null,
          reminderType: event.reminderType ?? null,
        });
      }
    }
    setPendingRecurrence(null);
  };

  const handleResyncChoice = (
    action: "this" | "all",
    pendingResync: PlanEvent,
  ) => {
    const baseId = pendingResync.detachedFromId;
    if (action === "this") {
      onResyncOccurrence?.(baseId!, pendingResync.date, pendingResync.id);
    } else {
      onResyncAllOccurrences?.(baseId!);
    }
    setPendingResync(null);
    setEditingEvent(null);
  };

  return { handleRecurrenceChoice, handleResyncChoice };
}
